import { useMemo } from "react";
import type { DataTableBlock, TableColumn } from "../contract/types";
import { formatCell, inferPercentScale, type PercentScale } from "../lib/format";
import type { BlockViewProps } from "./types";

/**
 * Tabla genérica. Respeta `align` y `format` por columna.
 *
 * Sobre los porcentajes: el contrato no fija si `0.185` es 18,5 % o 0,185 %.
 * La escala se decide por columna —si todos los valores caen dentro de ±1 se
 * leen como fracción— y la decisión se toma una vez para toda la columna, no
 * celda a celda, para que la tabla nunca mezcle criterios.
 */

function percentScaleFor(rows: Record<string, unknown>[], column: TableColumn): PercentScale {
  if (column.format !== "percent") return "fraction";
  return inferPercentScale(rows.map((row) => row[column.key]));
}

function totalByAlignment(
  rows: Record<string, unknown>[],
  column: TableColumn,
): string | null {
  if (column.format !== "number" && column.format !== "currency") return null;
  const values = rows
    .map((row) => row[column.key])
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (values.length !== rows.length || values.length === 0) return null;
  const total = values.reduce((sum, value) => sum + value, 0);
  return formatCell(total, column.format);
}

export function DataTable({ block }: BlockViewProps<DataTableBlock>) {
  const columns = block.columns ?? [];
  const rows = block.rows ?? [];
  const highlighted = new Set(block.highlight_rows ?? []);

  const scales = useMemo(() => {
    const map = new Map<string, PercentScale>();
    for (const column of columns) map.set(column.key, percentScaleFor(rows, column));
    return map;
  }, [columns, rows]);

  if (columns.length === 0) {
    return <p className="atom__empty">La tabla no trae columnas.</p>;
  }

  const numericColumns = columns.filter(
    (column) => column.format === "number" || column.format === "currency",
  );

  return (
    <figure className="atom atom--data-table data-table">
      <div className="data-table__scroll">
        <table className="data-table__table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`data-table__th data-table__cell--${column.align ?? "left"}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={highlighted.has(rowIndex) ? "data-table__row--highlight" : undefined}
              >
                {columns.map((column) => {
                  const raw = row[column.key];
                  const scale = scales.get(column.key) ?? "fraction";
                  const isNumeric = column.format !== "text" && column.format !== undefined;
                  const numeric = typeof raw === "number" ? raw : null;
                  return (
                    <td
                      key={column.key}
                      className={[
                        `data-table__cell--${column.align ?? (isNumeric ? "right" : "left")}`,
                        isNumeric ? "u-nums" : "",
                        numeric !== null && numeric < 0 ? "data-table__cell--negative" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {formatCell(raw, column.format, scale)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {numericColumns.length > 0 && rows.length > 2 ? (
            <tfoot>
              <tr>
                {columns.map((column, index) => {
                  const total = totalByAlignment(rows, column);
                  if (index === 0) {
                    return (
                      <td key={column.key} className="data-table__total-label">
                        Total
                      </td>
                    );
                  }
                  return (
                    <td
                      key={column.key}
                      className={`data-table__cell--${column.align ?? "right"} u-nums data-table__total`}
                    >
                      {total ?? ""}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      {block.caption ? <figcaption className="block-caption">{block.caption}</figcaption> : null}
    </figure>
  );
}

export default DataTable;
